import { isCollectionItemComplete, itemsTally, type ProgressItem } from "../lib/completion";
import type { CollectionItemStatus } from "../api/types";

// Replaces the single percentage ring (PortalProgressRing) on the Requests
// view across all four portals. A ring collapsed every distinct status into
// one number and silently counted "Under review" as complete; this surfaces
// the full breakdown so the recipient can see exactly where their items
// stand. "Complete" here means Approved or Waived ONLY — matching the
// backend package-level PortalPackageService.IsCompleted — so the meter and
// the per-status pills never disagree again.
//
// Brand rule: colours come from design tokens (status / gold / gray /
// border families), never hardcoded hex. Accessibility: status is never
// colour-only — every segment has a text label + count in the legend, and
// the bar carries role="img" + an aria-label summarising each segment.

// Approval chains (ruling R-C) — a chain item is counted by the caller's own
// chain status, never its item status: fully signed, your turn, or waiting on
// another party (which includes "you've done your step, others haven't").
type ChainSegment = "ChainCompleted" | "ChainYourTurn" | "ChainWaiting";
type SegmentKey = CollectionItemStatus | ChainSegment;

interface SegmentSpec {
  key: SegmentKey;
  label: string;
  /** Solid token-backed fill for the bar segment + legend swatch. */
  fillClass: string;
}

// Ordered Approved → … → Not started, with Waived as a muted tail (the
// segment order the redesign locks in). Chain segments sit beside their
// nearest non-chain meaning: fully signed by Approved, your turn with the
// to-do states, waiting on others by under review.
const SEGMENTS: SegmentSpec[] = [
  { key: "Approved", label: "Approved", fillClass: "bg-status-success" },
  { key: "ChainCompleted", label: "Fully signed", fillClass: "bg-status-success" },
  { key: "SubmittedForReview", label: "Under review", fillClass: "bg-gold" },
  { key: "ChainWaiting", label: "Waiting on others", fillClass: "bg-gold-tint" },
  { key: "ChangesRequested", label: "Needs changes", fillClass: "bg-status-warning" },
  { key: "ChainYourTurn", label: "Your turn", fillClass: "bg-status-teal" },
  { key: "InProgress", label: "In progress", fillClass: "bg-status-info" },
  { key: "Pending", label: "Not started", fillClass: "bg-gray-mid" },
  { key: "Waived", label: "No longer needed", fillClass: "bg-border-light" },
];

/** The segment an item counts in. A chain item that staff closed (Approved /
 *  Waived) without the chain completing counts by that status. */
function segmentOf(item: ProgressItem): SegmentKey {
  const chain = item.approvalChain;
  if (!chain) return item.status;
  if (chain.isCompleted) return "ChainCompleted";
  if (isCollectionItemComplete(item)) return item.status;
  return chain.myStatus === "YourTurn" ? "ChainYourTurn" : "ChainWaiting";
}

function tally(items: ProgressItem[]): Record<SegmentKey, number> {
  const counts: Record<SegmentKey, number> = {
    Pending: 0,
    InProgress: 0,
    SubmittedForReview: 0,
    ChangesRequested: 0,
    Approved: 0,
    Waived: 0,
    ChainCompleted: 0,
    ChainYourTurn: 0,
    ChainWaiting: 0,
  };
  for (const i of items) counts[segmentOf(i)] += 1;
  return counts;
}

export function PortalProgressBreakdown({
  items,
}: {
  items: ProgressItem[];
}) {
  const total = items.length;
  if (total === 0) return null;

  const counts = tally(items);
  const present = SEGMENTS.filter((s) => counts[s.key] > 0);

  // Headline math: "complete" comes from the canonical itemsTally /
  // isCollectionItemComplete (Approved + Waived only, or a completed chain),
  // so it can never drift from the predicate the rest of the portal counts
  // by. "Under review", "waiting on others" and "to do" are called out
  // separately so an item awaiting someone else is never silently folded
  // into "complete". The four partitions cover every segment, so they always
  // sum to total.
  const complete = itemsTally(items).done;
  const underReview = counts.SubmittedForReview;
  const waitingOnOthers = counts.ChainWaiting;
  const toDo =
    counts.Pending + counts.InProgress + counts.ChangesRequested + counts.ChainYourTurn;

  const headlineExtras = [
    underReview > 0 ? `${underReview} under review` : null,
    waitingOnOthers > 0 ? `${waitingOnOthers} waiting on others` : null,
    toDo > 0 ? `${toDo} to do` : null,
  ].filter(Boolean);

  const barSummary = present.map((s) => `${counts[s.key]} ${s.label}`).join(", ");

  return (
    <div
      className="flex flex-col gap-space-3 rounded-md border border-border-light bg-white-pure p-space-4"
      data-testid="requests-breakdown"
    >
      <p className="text-small font-medium text-onyx m-0">
        {complete} of {total} complete
        {headlineExtras.length > 0 && (
          <span className="text-gray-mid font-normal"> · {headlineExtras.join(" · ")}</span>
        )}
      </p>

      {/* Proportional bar (sm+). Each present status gets a segment sized by
          flex-grow so widths stay proportional without manual percentage
          math; a min-width keeps a tiny 1-of-N segment visible and tappable.
          Hidden on the narrowest viewports, where the legend below is the
          stacked fallback. */}
      <div
        className="hidden sm:flex h-space-3 w-full overflow-hidden rounded-pill"
        role="img"
        aria-label={`Item status breakdown: ${barSummary}`}
        data-testid="requests-breakdown-bar"
      >
        {present.map((s) => (
          <div
            key={s.key}
            className={`${s.fillClass} h-full min-w-space-2`}
            style={{ flexGrow: counts[s.key] }}
            data-status={s.key}
            data-count={counts[s.key]}
          />
        ))}
      </div>

      {/* Legend — always visible, wraps to a stacked list on narrow
          viewports. Each entry pairs a colour swatch with a text label and
          count, so status is never conveyed by colour alone. */}
      <ul className="flex flex-wrap gap-x-space-4 gap-y-space-2 list-none p-0 m-0" data-testid="requests-breakdown-legend">
        {present.map((s) => (
          <li key={s.key} className="flex items-center gap-space-2" data-status={s.key}>
            <span
              className={`${s.fillClass} inline-block h-space-2 w-space-2 rounded-sm border border-border-light`}
              aria-hidden="true"
            />
            <span className="text-caption text-gray-mid">
              {s.label} <span className="font-medium text-onyx">{counts[s.key]}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
